<script setup>
import { ref, computed, watch } from 'vue'
import axios from '../axios.js'
import PBTeamMappingModal from './PBTeamMappingModal.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'update:show'])

const teams = ref([])
const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const editingId = ref(null)
const showAddForm = ref(false)
const showPBTeamModal = ref(false)
const selectedTeam = ref(null)

// Form state
const formData = ref({
  team_name: '',
  league: '',
  region: '',
  league_id: null,
  pb_team_id: '',
  rgm_team_id: ''
})

// Filtered teams
const filteredTeams = computed(() => {
  if (!searchQuery.value.trim()) {
    return teams.value
  }
  const query = searchQuery.value.toLowerCase()
  return teams.value.filter(team => 
    team.team_name?.toLowerCase().includes(query) ||
    team.league?.toLowerCase().includes(query) ||
    team.region?.toLowerCase().includes(query) ||
    String(team.id).includes(query) ||
    String(team.league_id).includes(query)
  )
})

// Fetch all teams
const fetchTeams = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get('/api/team-data-management/teams')
    teams.value = response.data || []
  } catch (err) {
    error.value = 'Failed to load teams'
    console.error('Error fetching teams:', err)
  } finally {
    loading.value = false
  }
}

// Reset form
const resetForm = () => {
  formData.value = {
    team_name: '',
    league: '',
    region: '',
    league_id: null,
    pb_team_id: '',
    rgm_team_id: ''
  }
  editingId.value = null
  showAddForm.value = false
}

// Start editing
const startEdit = (team) => {
  editingId.value = team.id
  formData.value = {
    team_name: team.team_name || '',
    league: team.league || '',
    region: team.region || '',
    league_id: team.league_id || null,
    pb_team_id: team.pb_team_id || '',
    rgm_team_id: team.rgm_team_id || ''
  }
  showAddForm.value = true
}

// Open PB Team mapping modal
const openPBTeamModal = (team) => {
  selectedTeam.value = team
  showPBTeamModal.value = true
}

// Handle team updated from modal
const handleTeamUpdated = () => {
  fetchTeams()
}

// Cancel edit/add
const cancelEdit = () => {
  resetForm()
}

// Save team (create or update)
const saveTeam = async () => {
  if (!formData.value.team_name) {
    error.value = 'Team name is required'
    return
  }

  loading.value = true
  error.value = null

  try {
    if (editingId.value) {
      // Update existing
      await axios.put(`/api/team-data-management/teams/${editingId.value}`, formData.value)
    } else {
      // Create new
      await axios.post('/api/team-data-management/teams', formData.value)
    }
    
    await fetchTeams()
    resetForm()
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to save team'
    console.error('Error saving team:', err)
  } finally {
    loading.value = false
  }
}

// Delete team
const deleteTeam = async (id) => {
  if (!confirm('Are you sure you want to delete this team?')) {
    return
  }

  loading.value = true
  error.value = null

  try {
    await axios.delete(`/api/team-data-management/teams/${id}`)
    await fetchTeams()
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to delete team'
    console.error('Error deleting team:', err)
  } finally {
    loading.value = false
  }
}

// Close overlay
const closeOverlay = () => {
  emit('update:show', false)
  emit('close')
  resetForm()
}

// Watch for show prop changes
watch(() => props.show, (isOpen) => {
  if (isOpen) {
    fetchTeams()
    resetForm()
  }
})
</script>

<template>
  <div 
    v-if="show" 
    class="fixed inset-0 bg-black/60 z-[9998] flex items-center justify-center p-4"
    data-no-global-context-menu
  >
    <div class="absolute inset-0" @click="closeOverlay"></div>
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center space-x-3">
          <i class="fas fa-users text-pink-500 text-xl"></i>
          <div>
            <div class="text-lg font-semibold text-gray-800">Team Data Management</div>
            <div class="text-xs text-gray-500">Manage teams and their data</div>
          </div>
        </div>
        <button 
          class="p-2 rounded-full hover:bg-gray-100 text-gray-500" 
          @click="closeOverlay"
          title="Close"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Search and Add Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-shrink-0">
        <div class="flex-1 relative">
          <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input 
            v-model="searchQuery"
            type="text"
            class="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-pink-500"
            placeholder="Search by name, league, region, or ID..."
          />
        </div>
        <button
          @click="showAddForm = !showAddForm; editingId = null; if (!showAddForm) resetForm()"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-plus"></i>
          {{ showAddForm && !editingId ? 'Cancel' : 'Add New' }}
        </button>
      </div>

      <!-- Add/Edit Form -->
      <div v-if="showAddForm" class="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div class="bg-white rounded-lg p-4 shadow-sm">
          <h3 class="text-sm font-semibold mb-4 text-gray-700">
            {{ editingId ? 'Edit Team' : 'Add New Team' }}
          </h3>
          
          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {{ error }}
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs text-gray-600 mb-1">Team Name *</label>
              <input
                v-model="formData.team_name"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., Los Angeles Lakers"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">PB Team ID</label>
              <input
                v-model="formData.pb_team_id"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., lakers"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">RGM Team ID</label>
              <input
                v-model="formData.rgm_team_id"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., 12345"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">League</label>
              <input
                v-model="formData.league"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., NBA"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">Region</label>
              <input
                v-model="formData.region"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., USA"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">League ID</label>
              <input
                v-model.number="formData.league_id"
                type="number"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., 1"
              />
            </div>
          </div>

          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="cancelEdit"
              class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="saveTeam"
              :disabled="loading"
              class="px-4 py-2 text-sm bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? 'Saving...' : (editingId ? 'Update' : 'Create') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto p-6">
        <div v-if="loading && teams.length === 0" class="text-center py-8">
          <i class="fas fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
          <p class="text-sm text-gray-500">Loading teams...</p>
        </div>
        
        <div v-else-if="error && teams.length === 0" class="text-center py-8">
          <i class="fas fa-exclamation-circle text-2xl text-red-400 mb-2"></i>
          <p class="text-sm text-red-600">{{ error }}</p>
          <button
            @click="fetchTeams"
            class="mt-4 px-4 py-2 text-sm bg-pink-500 text-white rounded hover:bg-pink-600"
          >
            Retry
          </button>
        </div>

        <div v-else-if="!filteredTeams.length" class="text-center py-8">
          <i class="fas fa-inbox text-4xl text-gray-300 mb-2"></i>
          <p class="text-sm text-gray-500">
            {{ searchQuery ? 'No teams found matching your search' : 'No teams available' }}
          </p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-100 text-gray-700 uppercase text-xs">
              <tr>
                <th class="px-4 py-3 text-left">ID</th>
                <th class="px-4 py-3 text-left">Team Name</th>
                <th class="px-4 py-3 text-left">PB Team ID</th>
                <th class="px-4 py-3 text-left">RGM Team ID</th>
                <th class="px-4 py-3 text-left">League</th>
                <th class="px-4 py-3 text-left">Region</th>
                <th class="px-4 py-3 text-left">League ID</th>
                <th class="px-4 py-3 text-left">Created</th>
                <th class="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="team in filteredTeams"
                :key="team.id"
                class="hover:bg-gray-50 cursor-pointer"
                @click.stop.prevent="openPBTeamModal(team)"
              >
                <td class="px-4 py-3 font-medium text-gray-900">
                  {{ team.id }}
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ team.team_name || '-' }}
                </td>
                <td class="px-4 py-3">
                  <span v-if="team.pb_team_id" class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    {{ team.pb_team_id }}
                  </span>
                  <span v-else class="text-xs text-gray-400">-</span>
                </td>
                <td class="px-4 py-3">
                  <span v-if="team.rgm_team_id" class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {{ team.rgm_team_id }}
                  </span>
                  <span v-else class="text-xs text-gray-400">-</span>
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ team.league || '-' }}
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ team.region || '-' }}
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ team.league_id || '-' }}
                </td>
                <td class="px-4 py-3 text-gray-500">
                  {{ team.created_at ? new Date(team.created_at).toLocaleDateString() : '-' }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <button
                      @click.stop="startEdit(team)"
                      class="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      @click.stop="deleteTeam(team.id)"
                      class="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
        <div class="text-xs text-gray-500">
          Total: {{ teams.length }} team{{ teams.length !== 1 ? 's' : '' }}
          <span v-if="searchQuery && filteredTeams.length !== teams.length">
            ({{ filteredTeams.length }} filtered)
          </span>
        </div>
        <button
          @click="fetchTeams"
          :disabled="loading"
          class="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
        >
          <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- PB Team Mapping Modal -->
    <PBTeamMappingModal
      :show="showPBTeamModal"
      :team="selectedTeam"
      @update:show="showPBTeamModal = $event"
      @close="showPBTeamModal = false"
      @team-updated="handleTeamUpdated"
    />
  </div>
</template>

<style scoped>
</style>
